import {REACT_APP_CALCOM_ORIGIN} from '@progyny/utils/env'

// Every deploy workflow sets REACT_APP_CALCOM_ORIGIN *with* a trailing
// slash. A plain GET page URL tolerates the resulting double slash fine
// (e.g. getCancellationUrl, buildSlotsComparisonEmbedUrl below), but a
// POST request's exact path and a strict MessageEvent.origin comparison
// don't - those need the normalised form.
const CALCOM_ORIGIN = REACT_APP_CALCOM_ORIGIN.replace(/\/+$/, '')

export function getCancellationUrl(bookingUid: string): string {
  return `${REACT_APP_CALCOM_ORIGIN}/booking/${bookingUid}?cancel=true`
}

export interface EarliestEnglishSlotInput {
  eventTypeId?: number
  eventTypeSlug?: string
  usernameList?: string[]
  language: string
  startTime?: string
  endTime?: string
  duration?: number
  timeZone?: string
  isTeamEvent?: boolean
  orgSlug?: string | null
  routedTeamMemberIds?: number[] | null
  queuedFormResponseId?: string
}

export interface EarliestSlot {
  date: string
  time: string
}

export interface EarliestEnglishSlotResult {
  earliestEnglishSlot: EarliestSlot | null
  earliestSelectedLanguageSlot: EarliestSlot | null
}

const EMPTY_RESULT: EarliestEnglishSlotResult = {
  earliestEnglishSlot: null,
  earliestSelectedLanguageSlot: null,
}

const EARLIEST_ENGLISH_SLOT_MESSAGE_TYPE = 'calcom:earliestEnglishSlot'
const EARLIEST_ENGLISH_SLOT_TIMEOUT_MS = 8000

interface ParsedCalEvent {
  usernameList: string[]
  eventTypeSlug: string
  isTeamEvent: boolean
}

/**
 * Mirrors how cal.com's own Booker resolves a "team/{teamSlug}/{eventSlug}"
 * calLink into { usernameList, eventTypeSlug, isTeamEvent }.
 */
export function parseTeamCalLink(calLink: string): ParsedCalEvent | null {
  const segments = calLink.split('/').filter(Boolean)
  if (segments.length !== 3 || segments[0] !== 'team') {
    return null
  }
  const [, teamSlug, eventSlug] = segments
  return {usernameList: [teamSlug], eventTypeSlug: eventSlug, isTeamEvent: true}
}

/**
 * A calLink of the form "router?form={formId}" doesn't carry an event type
 * directly - progyny_cal.com's routing form picks one at submit time based
 * on the member's form answers (see getRoutedUrl.ts in progyny_cal.com).
 * Returns the formId, or null if calLink isn't a routing-form link.
 */
export function parseRouterFormId(calLink: string): string | null {
  const [path, queryString] = calLink.split('?')
  if (path.replace(/^\/+|\/+$/g, '') !== 'router') {
    return null
  }
  return new URLSearchParams(queryString ?? '').get('form')
}

/**
 * Mirrors getUserAndEventTypeSlug in progyny_cal.com's
 * getEventTypeRedirectUrl.ts, which is what builds the redirect path
 * /api/router resolves a routing form to: "team/{teamSlug}/{eventSlug}"
 * for a team event, or "{username}/{eventSlug}" for a user event.
 */
function parseResolvedEventPath(pathname: string): ParsedCalEvent | null {
  const teamEvent = parseTeamCalLink(pathname)
  if (teamEvent) return teamEvent

  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 2 && segments[0] !== 'team') {
    const [username, eventSlug] = segments
    return {
      usernameList: [username],
      eventTypeSlug: eventSlug,
      isTeamEvent: false,
    }
  }
  return null
}

export interface ResolvedRoutedEventParams extends ParsedCalEvent {
  routedTeamMemberIds?: number[]
  queuedFormResponseId?: string
}

const ROUTED_TEAM_MEMBER_IDS_PARAM = 'cal.routedTeamMemberIds'
const QUEUED_FORM_RESPONSE_ID_PARAM = 'cal.queuedFormResponseId'

/**
 * Mirrors progyny_cal.com's /router page (getRoutedUrl.ts): resolves a
 * routing form's formId + field responses into the concrete team/event
 * (and routing metadata) the real booking page would land on, via the
 * same public POST /api/router endpoint the booking page itself uses.
 *
 * Note: this intentionally doesn't forward the resolved
 * routingFormResponseId to the slots comparison - progyny_cal.com's
 * getEarliestEnglishSlot.schema.ts validates it with a non-coerced
 * z.number(), which always fails on the stringified query param value.
 */
export async function resolveRoutedEvent(
  formId: string,
  routingFormResponses: Record<string, string> | undefined,
  signal?: AbortSignal
): Promise<ResolvedRoutedEventParams | null> {
  try {
    const response = await fetch(`${CALCOM_ORIGIN}/api/router`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({form: formId, ...routingFormResponses}),
      signal,
    })
    if (!response.ok) return null

    const {status, data} = await response.json()
    if (status !== 'success' || !data?.redirect) return null

    const redirectUrl = new URL(data.redirect)
    const resolvedPath = parseResolvedEventPath(redirectUrl.pathname)
    if (!resolvedPath) return null

    const routedTeamMemberIdsParam = redirectUrl.searchParams.get(
      ROUTED_TEAM_MEMBER_IDS_PARAM
    )
    const queuedFormResponseId = redirectUrl.searchParams.get(
      QUEUED_FORM_RESPONSE_ID_PARAM
    )

    return {
      ...resolvedPath,
      ...(routedTeamMemberIdsParam && {
        routedTeamMemberIds: routedTeamMemberIdsParam.split(',').map(Number),
      }),
      ...(queuedFormResponseId && {queuedFormResponseId}),
    }
  } catch {
    return null
  }
}

function buildSlotsComparisonEmbedUrl(input: EarliestEnglishSlotInput): string {
  const params = new URLSearchParams()

  Object.entries(input).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    params.set(
      key,
      typeof value === 'object' ? JSON.stringify(value) : String(value)
    )
  })

  return `${REACT_APP_CALCOM_ORIGIN}/embed/slots-comparison?${params.toString()}`
}

/**
 * Requests the earliest-English-slot comparison via a hidden, same-purpose
 * iframe that relays its result back via postMessage, the same mechanism
 * the visible Cal.com booking iframe already uses to talk to member-portal.
 * This avoids a cross-origin fetch entirely, so no CORS configuration is
 * needed on progyny_cal.com's side.
 */
export function requestEarliestEnglishSlotViaEmbed(
  input: EarliestEnglishSlotInput,
  signal?: AbortSignal
): Promise<EarliestEnglishSlotResult> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = buildSlotsComparisonEmbedUrl(input)

    let settled = false

    const cleanup = () => {
      window.removeEventListener('message', handleMessage)
      clearTimeout(timeoutId)
      signal?.removeEventListener('abort', handleAbort)
      iframe.remove()
    }

    const settle = (result: EarliestEnglishSlotResult) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(result)
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== CALCOM_ORIGIN ||
        // Without this, a message from any same-origin Cal.com frame (e.g.
        // the visible booking iframe) would also be accepted here.
        event.source !== iframe.contentWindow ||
        event.data?.type !== EARLIEST_ENGLISH_SLOT_MESSAGE_TYPE
      ) {
        return
      }
      settle(event.data.payload ?? EMPTY_RESULT)
    }

    const handleAbort = () => settle(EMPTY_RESULT)

    const timeoutId = setTimeout(
      () => settle(EMPTY_RESULT),
      EARLIEST_ENGLISH_SLOT_TIMEOUT_MS
    )

    window.addEventListener('message', handleMessage)
    signal?.addEventListener('abort', handleAbort)
    document.body.appendChild(iframe)
  })
}
